import { useEffect } from 'react';
import { Input, useLocale } from 'shared-ui';
import { Controller, useFormContext } from 'react-hook-form';

import { Form } from 'ui/form/Form';
import { ReportModal } from '../reportModal/ReportModal';
import { OrganizationsTreeSelect } from 'ui/organizationsTreeSelect/OrganizationsTreeSelect';
import { TreeSingleSelect } from 'ui/organizationsTreeSelect/treeSingleSelect/TreeSingleSelect';
import { AppMessages } from 'i18n/messages';
import { ReactComponent as ReportPresentOwnersIcon } from 'assets/icons/report-present-owners.svg';
import { ReportTile } from '../reportTile/ReportTile';
import { usePermissions } from 'hooks/usePermissions/usePermissions';
import * as Styled from '../Reports.styles';
import { ReportErrorAlert } from '../reportErrorAlert';

import { PresentOwnersReportForm, PresentOwnersReportProps } from './PresentOwnersReport.types';

export const PresentOwnersReport = ({
  zones,
  isModalOpen,
  onModalClose,
  onModalOpen,
  onDownload,
  isLoadingDownload,
  hasNoDataError,
  hasNetworkError,
  onEmailModalOpen,
  isEmailModalOpen,
  isSmtpConfigured,
  onResetError,
}: PresentOwnersReportProps) => {
  const { formatMessage } = useLocale();
  const { perms } = usePermissions();

  const { control, watch } = useFormContext<PresentOwnersReportForm>();

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if ((hasNoDataError || hasNetworkError) && name) {
        onResetError();
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, hasNoDataError, hasNetworkError, onResetError]);

  return (
    <>
      {perms?.RAPORT_OSOB_OBECNYCH?.view && (
        <ReportTile
          icon={<ReportPresentOwnersIcon />}
          name={formatMessage({ id: AppMessages['reports.presentOwnersReport'] })}
          onClick={onModalOpen}
        />
      )}

      <ReportModal
        header={formatMessage({ id: AppMessages['reports.presentOwnersReport.title'] })}
        onClose={onModalClose}
        open={isModalOpen}
        onSend={onEmailModalOpen}
        onDownload={() => onDownload()}
        isLoading={isLoadingDownload}
        isSendButtonDisabled={isLoadingDownload || !isSmtpConfigured}
        isDownloadButtonDisabled={isLoadingDownload}
      >
        <Form.Grid gridTemplateColumns="1fr 1fr">
          <Controller
            name="nazwisko"
            control={control}
            render={({ field }) => (
              <Input
                label={formatMessage({ id: AppMessages['reports.eventsReport.lastName'] })}
                placeholder={formatMessage({
                  id: AppMessages['reports.presentOwnersReport.lastName.placeholder'],
                })}
                {...field}
              />
            )}
          />

          <Controller
            name="nrEwidencyjny"
            control={control}
            render={({ field }) => (
              <Input
                label={formatMessage({ id: AppMessages['reports.eventsReport.registrationNumber'] })}
                placeholder={formatMessage({
                  id: AppMessages['reports.presentOwnersReport.idNumber.placeholder'],
                })}
                {...field}
              />
            )}
          />

          <Styled.FieldContainer>
            <Controller
              name="komorka"
              control={control}
              render={({ field }) => (
                // Data permissions do not apply to this report, so the full organization tree is offered (contract R8).
                <OrganizationsTreeSelect
                  {...field}
                  multiple={false}
                  useFilteredData={false}
                  placeholder={formatMessage({ id: AppMessages['reports.presentOwnersReport.cell.placeholder'] })}
                  label={formatMessage({ id: AppMessages['reports.eventsReport.cell'] })}
                />
              )}
            />
          </Styled.FieldContainer>

          <Styled.FieldContainer>
            <Controller
              name="strefa"
              control={control}
              render={({ field }) => (
                <TreeSingleSelect
                  {...field}
                  options={zones}
                  disabled={!zones.length}
                  placeholder={formatMessage({ id: AppMessages['reports.presentOwnersReport.zone.placeholder'] })}
                  label={formatMessage({ id: AppMessages['reports.presentOwnersReport.zone'] })}
                />
              )}
            />
          </Styled.FieldContainer>
        </Form.Grid>

        {hasNoDataError && !isEmailModalOpen && (
          <ReportErrorAlert message={formatMessage({ id: AppMessages['reports.error.noData'] })} />
        )}

        {hasNetworkError && !isEmailModalOpen && (
          <ReportErrorAlert message={formatMessage({ id: AppMessages['reports.error.serverError'] })} />
        )}
      </ReportModal>
    </>
  );
};
